import React, { useContext, useEffect, useRef, useState } from 'react';
import AccountHeader from '../AccountHeader/AccountHeader';
import { useDispatch, useSelector } from 'react-redux';
import ContentApp from '../ContentApp';
import SearchIcon from '../icons/SearchIcon';
import { useTranslation } from 'react-i18next';
import styles from './Venues.module.css';
import { WelletContext } from '../../context/WelletContext';
import { getCookie } from '../../helpers/cookie';
import { fetchShow } from '../../actions/appActions';
import { SkeletonFilters, SkeletonVenueList } from '../Skeletons/SkeletonVenueList';
import Filter from './Filter';
import VenueCard from '../Common/VenueCard/VenueCard';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';
import { resetReservation } from '../../actions/reservationActions';
import CityDropdown from '../CityDropdown/CityDropdown';
import AccountHeaderConsumer from '../HeaderConsumer/HeaderConsumer';
import ChevronIcon from '../icons/chevronIcon';
import NavSelector from '../NavSelector/NavSelector';
import { useLocation } from 'react-router-dom';
import OrderVenues from './OrderVenues';

const Venues = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cityCookie = getCookie('defaultCity');
  const selectedCity = cityCookie
    ? JSON.parse(cityCookie)
    : { id: null, name: t('allFem'), nameLong: '' };
  const location = useLocation();
  const associate = useSelector((state) => state.associates);
  const app = useSelector((state) => state.app);
  const user = associate.user;
  const isCustomer = user?.roles?.includes('customer');
  const [filters, setFilters] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const welletContext = useContext(WelletContext);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [restaurants, setRestaurants] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState({
    id: 'FEATURED',
    name: t('orderVenues.FEATURED'),
  });
  const language = useSelector((state) => state.app.language);
  const queryParams = new URLSearchParams(location.search);
  const category = queryParams.get('category') || null;

  useEffect(() => {
    dispatch(resetReservation());
    getFilters();
    getRestaurants();
  }, []);

  const getRestaurants = async (categoryId, searchQuery, orderId) => {
    try {
      setLoadingRestaurants(true);
      const result = await fetchShow(
        app.language,
        selectedCity.id,
        1,
        100,
        welletContext,
        searchQuery,
        categoryId,
        orderId,
      );
      if (result.shows.length !== 0) {
        setRestaurants(result.shows);
      } else {
        setRestaurants([]);
      }
    } catch (error) {
      console.error(error);
    }
    setLoadingRestaurants(false);
  };

  const getFilters = async () => {
    try {
      const result = await welletContext.apis.tickets.get('/shows/categories', {
        params: {
          lang: language,
          cityId: selectedCity.id,
        },
      });
      setFilters(result.data);
      if (category) {
        const matchedFilter = result.data.find((filter) => filter.id === parseInt(category));
        if (matchedFilter) {
          setSelectedFilter(matchedFilter);
          getRestaurants(matchedFilter.id, searchValue);
        }
      }
    } catch (e) {
      console.error(e);
    }
    setLoadingFilters(false);
  };

  const debounce = function (func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const delayedSearch = useRef(
    debounce((query, categoryId) => {
      setSearchValue(query);
      getRestaurants(categoryId, query);
    }, 500),
  ).current;

  const renderVenues = () => {
    if (restaurants.length === 2) {
      return (
        <div
          className={`${styles.gridContainer} mt-3`}
          style={{ display: loadingRestaurants ? 'none' : '' }}
        >
          {restaurants.map((r, index) => (
            <VenueCard key={index} venue={r} />
          ))}
        </div>
      );
    }

    return (
      <>
        <div
          className={`${styles.gridContainer} ${styles.highlight} mt-3`}
          style={{ display: loadingRestaurants ? 'none' : '' }}
        >
          <VenueCard venue={restaurants[0]} />
        </div>
        <div
          className={`${styles.gridContainer} mt-3`}
          style={{ display: loadingRestaurants ? 'none' : '' }}
        >
          {restaurants.slice(1).map((r, index) => (
            <VenueCard key={index} venue={r} />
          ))}
        </div>
      </>
    );
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    if (value.length >= 3) {
      delayedSearch(value, selectedFilter ? selectedFilter.id : null);
    } else if (value.length === 0) {
      delayedSearch('', selectedFilter ? selectedFilter.id : null);
    }
  };

  const onFilterChange = (e) => {
    setSelectedFilter(e);
    setRestaurants(null);
    getRestaurants(e ? e.id : null, searchValue, selectedOrder ? selectedOrder.id : null);
  };

  const onOrderChange = (e) => {
    setSelectedOrder(e);
    setRestaurants(null);
    getRestaurants(selectedFilter ? selectedFilter.id : null, searchValue, e ? e.id : null);
  };

  return (
    <>
      {isCustomer ? (
        <AccountHeaderConsumer
          title={
            <div
              onClick={() => history.push(`/city-selector?events=/restaurant-list`)}
              className='text-white font-medium'
            >
              {selectedCity?.name} <ChevronIcon size='15' color='#fff' rotation={90} />
            </div>
          }
        />
      ) : (
        <AccountHeader
          title={<CityDropdown city={selectedCity} />}
          icon='config'
          pathName='/configuration'
        />
      )}

      <ContentApp menu header>
        <div className='row'>
          <div className='col position-relative'>
            <div className={styles.searchIcon}>
              <SearchIcon size='22' fillColor='#777777' />
            </div>
            <input
              type='text'
              placeholder={t('search')}
              className={styles.inputSearch}
              onChange={handleSearchInputChange}
            />
          </div>
          {/* <div className='col-auto card justify-content-center text-secondary mr-3'>{t('map')}</div> */}
        </div>

        {loadingFilters ? (
          <div className='mt-3'>
            <SkeletonFilters />
          </div>
        ) : (
          <>
            <div className={`${styles.highlight} fade-up`}>
              <div className='mt-3'>
                <Filter
                  filters={filters}
                  onFilterChange={onFilterChange}
                  selectedFilter={selectedFilter}
                />
              </div>
            </div>

            <div className={`${styles.highlight} fade-up`}>
              <div className='mt-2'>
                <OrderVenues onOrderChange={onOrderChange} selectedOrder={selectedOrder} />
              </div>
            </div>
          </>
        )}

        <div className='fade-up'>
          {loadingRestaurants && <SkeletonVenueList />}
          {restaurants?.length > 0
            ? renderVenues()
            : restaurants?.length === 0 &&
              !loadingRestaurants && (
                <div className='mt-3'>
                  <EmptyActivity
                    title={t('weAreSorry')}
                    subtitle={t('thereAreNoAvailableSpacesAtThisTime')}
                  />
                </div>
              )}
        </div>

        <NavSelector history={history} />
      </ContentApp>
    </>
  );
};

export default Venues;
